'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Save, X } from 'lucide-react'
import type { SkillResult } from '@creanizer/types'

interface SkillEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (updatedResult: Partial<SkillResult>) => void
  skillResult: SkillResult
  title?: string
}

export function SkillEditModal({
  isOpen,
  onClose,
  onSave,
  skillResult,
  title = '編集'
}: SkillEditModalProps) {
  const [editedName, setEditedName] = useState(skillResult.basicInfo.name)
  const [editedType, setEditedType] = useState(skillResult.basicInfo.type)
  const [editedCategory, setEditedCategory] = useState(skillResult.basicInfo.category)
  const [editedTier, setEditedTier] = useState(skillResult.basicInfo.tier)
  const [editedRarity, setEditedRarity] = useState(skillResult.basicInfo.rarity)
  const [editedLevel, setEditedLevel] = useState(skillResult.basicInfo.level)
  const [editedSummary, setEditedSummary] = useState(skillResult.basicInfo.summary)
  const [editedDescription, setEditedDescription] = useState(skillResult.basicInfo.description)
  const [editedCooldown, setEditedCooldown] = useState(skillResult.mechanics.timing.cooldown)
  const [editedRange, setEditedRange] = useState(skillResult.mechanics.targetingDetails.range)
  const [editedPrimaryEffect, setEditedPrimaryEffect] = useState(skillResult.effects.primaryEffect.description)

  // skillResultが変更されたら編集中の値をリセット
  useEffect(() => {
    setEditedName(skillResult.basicInfo.name)
    setEditedType(skillResult.basicInfo.type)
    setEditedCategory(skillResult.basicInfo.category)
    setEditedTier(skillResult.basicInfo.tier)
    setEditedRarity(skillResult.basicInfo.rarity)
    setEditedLevel(skillResult.basicInfo.level)
    setEditedSummary(skillResult.basicInfo.summary)
    setEditedDescription(skillResult.basicInfo.description)
    setEditedCooldown(skillResult.mechanics.timing.cooldown)
    setEditedRange(skillResult.mechanics.targetingDetails.range)
    setEditedPrimaryEffect(skillResult.effects.primaryEffect.description)
  }, [skillResult])

  const handleSave = () => {
    onSave({
      basicInfo: {
        ...skillResult.basicInfo,
        name: editedName,
        type: editedType,
        category: editedCategory,
        tier: editedTier,
        rarity: editedRarity,
        level: editedLevel,
        summary: editedSummary,
        description: editedDescription
      },
      mechanics: {
        ...skillResult.mechanics,
        timing: {
          ...skillResult.mechanics.timing,
          cooldown: editedCooldown
        },
        targetingDetails: {
          ...skillResult.mechanics.targetingDetails,
          range: editedRange
        }
      },
      effects: {
        ...skillResult.effects,
        primaryEffect: {
          ...skillResult.effects.primaryEffect,
          description: editedPrimaryEffect
        }
      }
    })
    onClose()
  }

  const handleCancel = () => {
    // 編集内容をリセット
    setEditedName(skillResult.basicInfo.name)
    setEditedType(skillResult.basicInfo.type)
    setEditedCategory(skillResult.basicInfo.category)
    setEditedTier(skillResult.basicInfo.tier)
    setEditedRarity(skillResult.basicInfo.rarity)
    setEditedLevel(skillResult.basicInfo.level)
    setEditedSummary(skillResult.basicInfo.summary)
    setEditedDescription(skillResult.basicInfo.description)
    setEditedCooldown(skillResult.mechanics.timing.cooldown)
    setEditedRange(skillResult.mechanics.targetingDetails.range)
    setEditedPrimaryEffect(skillResult.effects.primaryEffect.description)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">スキル名</Label>
              <Input
                id="name"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder="スキル名"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="level">レベル</Label>
              <Input
                id="level"
                type="number"
                value={editedLevel}
                onChange={(e) => setEditedLevel(parseInt(e.target.value) || 1)}
                placeholder="レベル"
                className="mt-1"
                min={1}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="type">タイプ</Label>
              <Input
                id="type"
                value={editedType}
                onChange={(e) => setEditedType(e.target.value)}
                placeholder="スキルタイプ"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="category">カテゴリー</Label>
              <Input
                id="category"
                value={editedCategory}
                onChange={(e) => setEditedCategory(e.target.value)}
                placeholder="カテゴリー"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="tier">ティア</Label>
              <Select value={editedTier} onValueChange={setEditedTier}>
                <SelectTrigger id="tier" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">初級 (Basic)</SelectItem>
                  <SelectItem value="intermediate">中級 (Intermediate)</SelectItem>
                  <SelectItem value="advanced">上級 (Advanced)</SelectItem>
                  <SelectItem value="master">達人級 (Master)</SelectItem>
                  <SelectItem value="hero">英雄級 (Hero)</SelectItem>
                  <SelectItem value="legendary">伝説級 (Legendary)</SelectItem>
                  <SelectItem value="mythic">神話級 (Mythic)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="rarity">レアリティ</Label>
            <Select value={editedRarity} onValueChange={setEditedRarity}>
              <SelectTrigger id="rarity" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="common">コモン (Common)</SelectItem>
                <SelectItem value="uncommon">アンコモン (Uncommon)</SelectItem>
                <SelectItem value="rare">レア (Rare)</SelectItem>
                <SelectItem value="epic">エピック (Epic)</SelectItem>
                <SelectItem value="legendary">レジェンダリー (Legendary)</SelectItem>
                <SelectItem value="mythic">ミシック (Mythic)</SelectItem>
                <SelectItem value="unique">ユニーク (Unique)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="summary">概要</Label>
            <Textarea
              id="summary"
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              placeholder="スキルの概要"
              rows={2}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="description">詳細説明</Label>
            <Textarea
              id="description"
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              placeholder="詳細な説明"
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cooldown">クールダウン</Label>
              <Input
                id="cooldown"
                value={editedCooldown}
                onChange={(e) => setEditedCooldown(e.target.value)}
                placeholder="クールダウン時間"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="range">射程</Label>
              <Input
                id="range"
                value={editedRange}
                onChange={(e) => setEditedRange(e.target.value)}
                placeholder="射程距離"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="primaryEffect">主要効果</Label>
            <Textarea
              id="primaryEffect"
              value={editedPrimaryEffect}
              onChange={(e) => setEditedPrimaryEffect(e.target.value)}
              placeholder="主要な効果の説明"
              rows={3}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            キャンセル
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}